import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowRight } from 'lucide-react';

type Phase = 'idle' | 'typing' | 'submitted' | 'reading' | 'generating' | 'done';

const TYPE_INTERVAL_MS = 22;
const ENTER_PAUSE_MS = 360;
const READ_DURATION_MS = 700;
const GEN_DURATION_MS = 2400;

function prefersReducedMotion(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Image-to-image demo: an attached can mockup drops into the terminal, the
 * agent generates a hero, and the result lands inside a fake browser to the
 * left. Mirrors the previous section's layout — terminal on the right,
 * payoff on the left — so the page alternates visually.
 *
 * Rendered as a `<div>` to keep the landing-page landmark count stable.
 */
export function AgentInputDemoSection() {
  const { t } = useTranslation('landing');

  const prompt = t('agentInputDemo.userPrompt');
  const reduceMotion = useMemo(prefersReducedMotion, []);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>(reduceMotion ? 'done' : 'idle');
  const [typed, setTyped] = useState(reduceMotion ? prompt.length : 0);

  useEffect(() => {
    if (reduceMotion || started) return;
    const node = wrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [reduceMotion, started]);

  useEffect(() => {
    if (!started || reduceMotion) return;
    const timers: ReturnType<typeof setTimeout>[] = [];

    setPhase('typing');
    for (let i = 1; i <= prompt.length; i++) {
      timers.push(setTimeout(() => setTyped(i), i * TYPE_INTERVAL_MS));
    }
    const typeEnd = prompt.length * TYPE_INTERVAL_MS;
    let t0 = typeEnd + ENTER_PAUSE_MS;
    timers.push(setTimeout(() => setPhase('submitted'), t0));
    t0 += 220;
    timers.push(setTimeout(() => setPhase('reading'), t0));
    t0 += READ_DURATION_MS;
    timers.push(setTimeout(() => setPhase('generating'), t0));
    t0 += GEN_DURATION_MS;
    timers.push(setTimeout(() => setPhase('done'), t0));

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [started, reduceMotion, prompt.length]);

  const typedSlice = phase === 'typing' ? prompt.slice(0, typed) : prompt;
  const showPromptLine = phase !== 'idle';
  const showReading = phase === 'reading' || phase === 'generating' || phase === 'done';
  const showGenerating = phase === 'generating' || phase === 'done';
  const showDone = phase === 'done';
  const browserVisible = phase === 'done';
  // While the result isn't ready, the terminal sits centered (full focus on
  // the prompt being typed). The moment the browser fades in, the terminal
  // slides to its final right-side position. Translate maths: terminal is
  // anchored at right-0 and is 44% of parent width — moving 64% of element
  // width left places its right edge at the parent's horizontal center.
  const terminalCentered = phase !== 'done';

  return (
    <div className="mx-auto w-full max-w-[1280px] px-6 py-20" ref={wrapRef}>
      {/* Eyebrow + heading */}
      <div className="mx-auto max-w-3xl text-center">
        <span className="font-mono text-caption uppercase tracking-[0.25em] text-accent-rose">
          {t('agentInputDemo.eyebrow')}
        </span>
        <h2 className="mt-3 text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-primary">
          <span className="agb-shine-text">{t('agentInputDemo.heading')}</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-body text-secondary">
          {t('agentInputDemo.subheadline')}
        </p>
      </div>

      {/* Stage. Mobile: terminal first, browser below. Desktop: browser left,
          terminal right + nudged down to overlap the browser's right edge. */}
      <div className="relative mx-auto mt-14 flex max-w-[1180px] flex-col gap-8 lg:block lg:min-h-[560px]">
        {/* ── Terminal (RIGHT on desktop) ─────────────────────────────── */}
        <div
          className={`order-1 transition-transform duration-700 ease-out lg:absolute lg:right-0 lg:top-[18%] lg:z-20 lg:w-[44%] ${
            terminalCentered ? 'lg:-translate-x-[64%]' : 'lg:translate-x-2'
          }`}
        >
          <div className="relative">
            {/* Outer glow ring */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-xl"
              style={{
                background:
                  'linear-gradient(140deg, rgba(219,147,176,0.4), rgba(37,110,255,0.18) 45%, transparent 75%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-surface shadow-[0_40px_120px_-30px_rgba(219,147,176,0.35)]">
              {/* Title bar — compact */}
              <div className="flex items-center gap-2 border-b border-white/[0.06] px-3.5 py-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-accent-red/80" role="presentation" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#f5c542]/80" role="presentation" />
                <div className="h-2.5 w-2.5 rounded-full bg-accent-lime/80" role="presentation" />
                <span className="mx-auto -translate-x-4 font-mono text-[10px] text-muted">
                  <span className="sr-only">Terminal · </span>
                  {t('agentInputDemo.windowTitle')}
                </span>
              </div>

              {/* Body */}
              <pre
                className="relative min-h-[360px] px-5 py-5 font-mono text-mono leading-relaxed sm:min-h-[400px]"
                aria-live="polite"
              >
                {/* Attached input — sized up so it reads as the source asset
                    the agent is generating from. Mirrors how Claude Code
                    shows pasted images in-thread. */}
                <div className="not-italic">
                  <div className="mb-4 flex items-center gap-3 rounded-lg border border-accent-rose/30 bg-canvas/70 p-3">
                    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md bg-[#f6f1ea]">
                      <img
                        src="/agentdrink-can.png"
                        alt={t('agentInputDemo.inputAlt')}
                        className="absolute inset-0 h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <span className="flex flex-col gap-0.5 leading-tight">
                      <span className="font-mono text-caption uppercase tracking-[0.2em] text-accent-rose">
                        input
                      </span>
                      <span className="text-primary">{t('agentInputDemo.attachmentName')}</span>
                      <span className="text-caption text-muted">
                        {t('agentInputDemo.attachmentMeta')}
                      </span>
                    </span>
                  </div>
                </div>

                {/* User prompt — wraps thanks to whitespace-pre-wrap below */}
                <div className="whitespace-pre-wrap text-primary">
                  <span className="text-accent-rose">{'> '}</span>
                  {showPromptLine ? (
                    <span>{renderPromptWithMention(typedSlice)}</span>
                  ) : (
                    <span className="text-muted">…</span>
                  )}
                  {phase === 'typing' && <span className="agb-terminal-caret-rose" />}
                </div>

                {showReading && (
                  <div className="mt-3 text-accent-cyan">{t('agentInputDemo.agentReading')}</div>
                )}

                {showGenerating && (
                  <div className="mt-1 text-accent-cyan">
                    {t('agentInputDemo.agentGenerating')}{' '}
                    {phase === 'generating' && (
                      <span className="agb-terminal-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                      </span>
                    )}
                  </div>
                )}

                {showDone && (
                  <>
                    <div className="mt-2 text-accent-lime">{t('agentInputDemo.agentDone')}</div>
                    <div className="text-secondary"> 1600×900 · 9 tokens · stage 2 · refined</div>
                  </>
                )}

                {(phase === 'submitted' ||
                  phase === 'reading' ||
                  phase === 'generating' ||
                  phase === 'done') && <span className="agb-terminal-caret-rose-static" />}
              </pre>
            </div>
          </div>
        </div>

        {/* ── Fake browser (LEFT on desktop) ──────────────────────────── */}
        <div className="order-2 lg:w-[64%]">
          <div
            className={`relative transition-all duration-700 ease-out ${
              browserVisible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
            }`}
            aria-hidden={!browserVisible}
          >
            {/* Outer glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-xl"
              style={{
                background:
                  'linear-gradient(140deg, rgba(45,225,252,0.3), rgba(219,147,176,0.18) 50%, transparent 80%)',
                padding: 1,
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <div className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-surface shadow-[0_40px_120px_-30px_rgba(45,225,252,0.3)]">
              {/* Browser chrome — thin */}
              <div className="flex items-center gap-2.5 border-b border-white/[0.06] bg-canvas/40 px-3 py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-accent-red/80" role="presentation" />
                  <div className="h-2.5 w-2.5 rounded-full bg-[#f5c542]/80" role="presentation" />
                  <div className="h-2.5 w-2.5 rounded-full bg-accent-lime/80" role="presentation" />
                </div>
                <div className="flex h-5 flex-1 items-center gap-1.5 rounded border border-white/[0.06] bg-canvas/70 px-2 font-mono text-[10px] tracking-tight text-secondary">
                  <span className="h-1 w-1 rounded-full bg-accent-lime/80" aria-hidden="true" />
                  {t('agentInputDemo.browserUrl')}
                </div>
              </div>

              {/* Hero canvas — generated image fills the whole page; the
                  header, CTA and learn-more arrow are overlaid transparently
                  so the AI-generated artwork stays the focal point. */}
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#f6f1ea]">
                <img
                  src="/agentdrink-hero.png"
                  alt={t('agentInputDemo.heroAlt')}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />

                {/* Slogan + CTA stack — lower-left of the hero. The slogan
                    sits directly above the Order now button so they read
                    as one block. */}
                <div className="absolute bottom-[14%] left-[6%] flex flex-col items-start gap-2.5">
                  <span
                    className="text-[28px] font-semibold leading-[1.05] tracking-tight text-[#1a1a1a]"
                    style={{
                      textShadow:
                        '0 1px 0 rgba(255,255,255,0.95), 0 2px 6px rgba(255,255,255,0.7), 0 0 28px rgba(255,255,255,0.55), 0 2px 10px rgba(0,0,0,0.18)',
                    }}
                  >
                    {t('agentInputDemo.slogan')}
                  </span>
                  <button
                    type="button"
                    tabIndex={-1}
                    className="agb-cta-jitter pointer-events-none inline-flex items-center gap-1.5 rounded-md bg-[#1a1a1a] px-3.5 py-2 text-small font-medium text-[#f6f1ea] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]"
                  >
                    {t('agentInputDemo.orderCta')}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>

                {/* Subtle bottom gradient so the Learn more chip reads cleanly */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent"
                />
                {/* Learn more arrow */}
                <div className="absolute inset-x-0 bottom-2.5 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 font-mono text-caption uppercase tracking-[0.25em] text-white/90">
                    {t('agentInputDemo.learnMore')}
                    <ArrowDown className="h-3.5 w-3.5 animate-bounce-soft" aria-hidden="true" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes agb-terminal-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .agb-terminal-caret-rose,
        .agb-terminal-caret-rose-static {
          display: inline-block;
          width: 0.6ch;
          height: 1em;
          margin-left: 0.1em;
          vertical-align: -0.12em;
          background: var(--color-accent-rose);
          animation: agb-terminal-blink 1s step-end infinite;
        }
        @keyframes agb-bounce-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(3px); }
        }
        .animate-bounce-soft { animation: agb-bounce-soft 1.8s ease-in-out infinite; }
        /* Periodic "once-at-a-time" jitter — pure rotation around the
           button's center. Holds still for most of the cycle, then a
           short oscillating wobble. */
        @keyframes agb-cta-jitter {
          0%, 86%, 100% { transform: rotate(0deg); }
          88% { transform: rotate(-5deg); }
          90% { transform: rotate(5deg); }
          92% { transform: rotate(-4deg); }
          94% { transform: rotate(3deg); }
          96% { transform: rotate(-2deg); }
          98% { transform: rotate(1deg); }
        }
        .agb-cta-jitter { animation: agb-cta-jitter 4.2s ease-in-out infinite; transform-origin: center center; }
        @media (prefers-reduced-motion: reduce) {
          .agb-terminal-caret-rose,
          .agb-terminal-caret-rose-static,
          .animate-bounce-soft,
          .agb-cta-jitter { animation: none; }
        }
      `}</style>
    </div>
  );
}

/**
 * Highlights `@filename.ext` mentions in the prompt the same way Claude Code
 * surfaces file references — keeps the in-prompt callout from disappearing
 * into a wall of mono text.
 */
function renderPromptWithMention(text: string) {
  const re = /@[\w./-]+/g;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <span key={key++} className="text-accent-cyan">
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
